import { Fragment } from 'react'
import { Check, Minus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

import { LOGO_EXPORT_PRICE_LABEL } from '@/constants/exports'
import { unlimitedMonthlyPrice, unlimitedYearlyPrice } from '@/constants/site'

type PlanKey = 'single' | 'monthly' | 'yearly'

interface PricingComparisonTableProps {
    highlightPlan?: PlanKey
    onBuyLogo?: () => void
    onMonthlyPlan?: () => void
    onYearlyPlan?: () => void
    userStatus?: {
        isSubscriber: boolean
        subscriptionStatus: string
    }
}

interface FeatureRow {
    name: string
    single: boolean | string
    monthly: boolean | string
    yearly: boolean | string
}

interface FeatureCategory {
    category: string
    items: FeatureRow[]
}

const yearlySaving = unlimitedMonthlyPrice * 12 - unlimitedYearlyPrice

const features: FeatureCategory[] = [
    {
        category: 'Designing',
        items: [
            {
                name: 'Logo designs',
                single: 'One per payment',
                monthly: 'Unlimited',
                yearly: 'Unlimited'
            },
            { name: 'Concepts per design', single: '3', monthly: '3', yearly: '3' },
            { name: 'Every logo type and style', single: true, monthly: true, yearly: true },
            { name: 'Custom color palettes', single: true, monthly: true, yearly: true }
        ]
    },
    {
        category: 'Files you get',
        items: [
            { name: 'Full-resolution PNG', single: true, monthly: true, yearly: true },
            { name: 'SVG vector file', single: true, monthly: true, yearly: true },
            { name: 'PDF vector file', single: true, monthly: true, yearly: true },
            { name: 'EPS vector file', single: true, monthly: true, yearly: true },
            { name: 'No watermark', single: true, monthly: true, yearly: true },
            { name: 'Commercial use', single: true, monthly: true, yearly: true }
        ]
    },
    {
        category: 'Privacy',
        items: [
            { name: 'Private logos', single: false, monthly: true, yearly: true }
        ]
    },
    {
        category: 'Billing',
        items: [
            {
                name: 'Payment',
                single: 'Once, per logo',
                monthly: 'Every month',
                yearly: 'Once a year'
            },
            { name: 'Cancel anytime', single: 'Nothing to cancel', monthly: true, yearly: true }
        ]
    }
]

function renderCell(value: boolean | string) {
    if (typeof value === 'boolean') {
        return value ? (
            <span className='inline-flex items-center justify-center'>
                <Check className='size-4 text-success' aria-hidden />
                <span className='sr-only'>Included</span>
            </span>
        ) : (
            <span className='inline-flex items-center justify-center'>
                <Minus className='size-4 text-ink-subtle' aria-hidden />
                <span className='sr-only'>Not included</span>
            </span>
        )
    }
    return <span className='text-small text-ink-muted'>{value}</span>
}

export function PricingComparisonTable({
    highlightPlan = 'single',
    onBuyLogo,
    onMonthlyPlan,
    onYearlyPlan,
    userStatus
}: PricingComparisonTableProps) {
    const col = (plan: PlanKey) =>
        cn('px-4 py-3 text-center align-middle', highlightPlan === plan && 'bg-surface-2')

    const head = (plan: PlanKey) =>
        cn('px-4 py-5 text-center align-bottom', highlightPlan === plan && 'bg-surface-2')

    return (
        <div className='w-full overflow-x-auto rounded-lg border border-line bg-surface'>
            <table className='w-full min-w-[640px] border-collapse'>
                <caption className='sr-only'>
                    Feature comparison across the single logo purchase and the monthly and
                    yearly unlimited plans
                </caption>
                <thead>
                    <tr className='border-b border-line-strong'>
                        <th scope='col' className='px-4 py-5 text-left'>
                            <span className='type-eyebrow'>Features</span>
                        </th>
                        <th scope='col' className={head('single')}>
                            <div className='text-small font-medium text-ink'>Single logo</div>
                            <div className='mt-1 font-display text-h3 text-ink'>
                                {LOGO_EXPORT_PRICE_LABEL}
                            </div>
                            <div className='text-caption text-ink-subtle'>one time, per logo</div>
                        </th>
                        <th scope='col' className={head('monthly')}>
                            <div className='text-small font-medium text-ink-muted'>
                                Unlimited monthly
                            </div>
                            <div className='mt-1 font-display text-h3 text-ink'>
                                ${unlimitedMonthlyPrice}
                            </div>
                            <div className='text-caption text-ink-subtle'>per month</div>
                        </th>
                        <th scope='col' className={head('yearly')}>
                            <div className='text-small font-medium text-ink-muted'>
                                Unlimited yearly
                            </div>
                            <div className='mt-1 font-display text-h3 text-ink'>
                                ${unlimitedYearlyPrice}
                            </div>
                            <div className='text-caption text-success'>
                                Saves ${yearlySaving} a year
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {features.map((category) => (
                        <Fragment key={category.category}>
                            <tr className='bg-surface-2'>
                                <td colSpan={4} className='px-4 py-3'>
                                    <span className='type-eyebrow'>{category.category}</span>
                                </td>
                            </tr>
                            {category.items.map((feature) => (
                                <tr
                                    key={feature.name}
                                    className='border-b border-line transition-colors duration-150 ease-out hover:bg-surface-2'
                                >
                                    <th
                                        scope='row'
                                        className='px-4 py-3 text-left text-small font-normal text-ink'
                                    >
                                        {feature.name}
                                    </th>
                                    <td className={col('single')}>{renderCell(feature.single)}</td>
                                    <td className={col('monthly')}>
                                        {renderCell(feature.monthly)}
                                    </td>
                                    <td className={col('yearly')}>{renderCell(feature.yearly)}</td>
                                </tr>
                            ))}
                        </Fragment>
                    ))}

                    <tr className='border-t border-line-strong'>
                        <th scope='row' className='px-4 py-6 text-left'>
                            <span className='type-eyebrow'>Choose</span>
                        </th>
                        <td className={cn('px-4 py-6 text-center', highlightPlan === 'single' && 'bg-surface-2')}>
                            <Button onClick={onBuyLogo} size='sm' className='w-full max-w-[180px]'>
                                Make a logo
                            </Button>
                        </td>
                        <td className={cn('px-4 py-6 text-center', highlightPlan === 'monthly' && 'bg-surface-2')}>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={onMonthlyPlan}
                                disabled={userStatus?.isSubscriber}
                                className='w-full max-w-[180px]'
                            >
                                {userStatus?.isSubscriber ? 'Current plan' : 'Subscribe monthly'}
                            </Button>
                        </td>
                        <td className={cn('px-4 py-6 text-center', highlightPlan === 'yearly' && 'bg-surface-2')}>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={onYearlyPlan}
                                disabled={userStatus?.isSubscriber}
                                className='w-full max-w-[180px]'
                            >
                                {userStatus?.isSubscriber ? 'Current plan' : 'Subscribe yearly'}
                            </Button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}
